<template>
  <div class="partnerPerformanceInfo" v-loading="loading">
    <!-- L1 / L2 子 Tab -->
    <div class="partnerPerformanceInfoSubTabs">
      <button
        v-for="tab in visibleLevelTabs"
        :key="tab.value"
        type="button"
        class="partnerPerformanceInfoSubTab"
        :class="{ 'is-active': activeLevel === tab.value }"
        @click="switchLevelTab(tab.value)"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- 商务政策与账号/License 字段展示 -->
    <div class="partnerPerformanceInfoPolicyWrap">
      <!-- <div class="partnerPerformanceInfoPolicyRow">
        <span class="policyLabel">{{ activeLevel }}业绩的商务政策：</span>
        <span class="policyValue">{{
          activeLevel === "L1" ? l1Policy : l2Policy
        }}</span>
      </div> -->

      <div
        v-if="activeLevel === 'L1'"
        class="partnerPerformanceInfoPolicyRow mt-12"
      >
        <span class="policyLabel">已申请License：</span>
        <span class="policyValue highlight">{{ l1AppliedLicense || "-" }}</span>
      </div>
      <div
        v-else-if="activeLevel === 'L2'"
        class="partnerPerformanceInfoPolicyRow mt-12"
      >
        <span class="policyLabel">L-Design账号：</span>
        <span class="policyValue highlight">{{ l2LdesignAccount || "-" }}</span>
      </div>
    </div>

    <!-- 年度业绩表格 -->
    <el-table
      class="partnerPerformanceInfoTable"
      :data="tableRows"
      border
      stripe
      empty-text="暂无业绩数据"
    >
      <el-table-column
        label="授权期内有效业绩期"
        min-width="180"
        align="center"
      >
        <template slot-scope="scope">
          {{ formatPeriod(scope.row.startDate, scope.row.endDate) }}
        </template>
      </el-table-column>
      <el-table-column
        v-if="activeLevel === 'L2'"
        prop="seaCount"
        label="SEA数智盘柜台数"
        min-width="120"
        align="center"
      />
      <el-table-column
        label="业绩金额(智能化清单)"
        min-width="150"
        align="center"
      >
        <template slot-scope="scope">
          {{
            scope.row.performanceAmount != null
              ? formatAmount(scope.row.performanceAmount)
              : ""
          }}
        </template>
      </el-table-column>
      <el-table-column label="返利系数" min-width="100" align="center">
        <template slot-scope="scope">
          {{ formatCoefficient(scope.row.rebateCoefficient) }}
        </template>
      </el-table-column>
      <el-table-column label="可获得返利额(元)" min-width="150" align="center">
        <template slot-scope="scope">
          <span class="performanceInfoAmount">
            {{
              scope.row.availableRebate != null
                ? formatAmount(scope.row.availableRebate)
                : ""
            }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="返利额确认状态" min-width="130" align="center">
        <template slot-scope="scope">
          <span v-if="scope.row.rebateConfirm === 1" class="status-confirmed"
            >已确认</span
          >
          <span
            v-else-if="scope.row.rebateConfirm === 0"
            class="status-unconfirmed"
            >待确认</span
          >
          <span v-else>--</span>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { getSysConfigList } from "@/api/lx";

const LEVEL_TABS = [
  { label: "L1业绩信息", value: "L1" },
  { label: "L2业绩信息", value: "L2" },
];

const STAT_CARDS = [
  { key: "inspect", label: "质检次数", field: "inspectCount" },
  { key: "qualified", label: "合格次数", field: "qualifiedCount" },
  { key: "unqualified", label: "不合格次数", field: "unqualifiedCount" },
];

const EMPTY_LEVEL_DATA = {
  qualityStats: {
    inspectCount: 0,
    qualifiedCount: 0,
    unqualifiedCount: 0,
  },
  rebateList: [],
};

/**
 * 合作伙伴详情 - 业绩信息（只读）
 * 对应设计稿：L1/L2/L3/L4 Tab、质检统计、年度返利表格
 */
export default {
  name: "PartnerPerformanceInfo",
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      activeLevel: "L1",
      levelTabList: LEVEL_TABS,
      statCards: STAT_CARDS,
      performanceDataMap: {
        L1: {
          ...EMPTY_LEVEL_DATA,
          qualityStats: { ...EMPTY_LEVEL_DATA.qualityStats },
        },
        L2: {
          ...EMPTY_LEVEL_DATA,
          qualityStats: { ...EMPTY_LEVEL_DATA.qualityStats },
        },
      },
      l1Policy: "",
      l2Policy: "",
    };
  },
  computed: {
    visibleLevelTabs() {
      const list = this.data.equityClassifyList;
      if (!Array.isArray(list) || !list.length) {
        return [];
      }
      const names = list.map((item) => item.equityClassifyName);
      return this.levelTabList.filter((tab) => names.includes(tab.value));
    },
    currentLevelData() {
      return this.performanceDataMap[this.activeLevel] || EMPTY_LEVEL_DATA;
    },
    l1AppliedLicense() {
      const d = this.data || {};
      const l1 = (d.equityClassifyList || []).find(
        (item) => item.equityClassifyName === "L1"
      );
      return l1 ? l1.appliedLicense : "-";
    },
    l2LdesignAccount() {
      const d = this.data || {};
      const l2 = (d.equityClassifyList || []).find(
        (item) => item.equityClassifyName === "L2"
      );
      return l2 ? l2.ldesignAccount : "-";
    },

    tableRows() {
      const list = this.currentLevelData.rebateList || [];
      if (!list.length) return [];

      const rows = list.map((item) => ({ ...item }));

      return rows;
    },
  },
  watch: {
    data: {
      handler() {
        this.initPerformanceData();
      },
      deep: true,
      immediate: true,
    },
    visibleLevelTabs: {
      handler(tabs) {
        if (!tabs.some((t) => t.value === this.activeLevel)) {
          this.activeLevel = tabs[0] ? tabs[0].value : "L1";
        }
      },
      immediate: true,
    },
  },
  created() {
    this.fetchSysConfig();
  },
  methods: {
    /**
     * 切换 L1 / L2 业绩分类 Tab
     * @param {string} level - 选中等级标识 L1 | L2
     * @returns {void}
     */
    switchLevelTab(level) {
      if (this.activeLevel === level) return;
      this.activeLevel = level;
    },

    /**
     * 初始化初始化业绩数据映射
     * @returns {void}
     */
    initPerformanceData() {
      this.loading = true;
      const fromApi = this.parseApiPerformanceData();
      this.performanceDataMap = fromApi || {
        L1: {
          qualityStats: {
            inspectCount: 0,
            qualifiedCount: 0,
            unqualifiedCount: 0,
          },
          rebateList: [],
        },
        L2: {
          qualityStats: {
            inspectCount: 0,
            qualifiedCount: 0,
            unqualifiedCount: 0,
          },
          rebateList: [],
        },
      };
      this.loading = false;
    },

    /**
     * 解析接口返回的业绩结构
     * @returns {Object|null} 格式化后的按等级映射的业绩对象，无数据则返回 null
     */
    parseApiPerformanceData() {
      const d = this.data || {};
      if (Array.isArray(d.equityClassifyList) && d.equityClassifyList.length) {
        const result = {
          L1: {
            qualityStats: {
              inspectCount: 0,
              qualifiedCount: 0,
              unqualifiedCount: 0,
            },
            rebateList: [],
          },
          L2: {
            qualityStats: {
              inspectCount: 0,
              qualifiedCount: 0,
              unqualifiedCount: 0,
            },
            rebateList: [],
          },
        };
        d.equityClassifyList.forEach((classify) => {
          const name = classify.equityClassifyName; // 例如 "L1", "L2"
          if (name && result[name]) {
            result[name] = {
              qualityStats: {
                inspectCount: classify.qualityInspectionCount || 0,
                qualifiedCount: classify.passCount || 0,
                unqualifiedCount: classify.failCount || 0,
              },
              rebateList: (classify.performanceList || []).map((perf) => ({
                id: perf.id,
                year: perf.year || "",
                startDate: perf.startDate || "",
                endDate: perf.endDate || "",
                performanceAmount: perf.performanceAmount ?? null,
                rebateCoefficient: perf.rebateCoefficient ?? null,
                availableRebate: perf.availableRebate ?? null,
                seaCount: perf.seaCount ?? null, // L2 级别的 SEA数智盘柜台数
                rebateConfirm: perf.rebateConfirm ?? null,
              })),
            };
          }
        });
        return result;
      }
      return null;
    },

    /**
     * 异步拉取系统商务政策配置
     * @returns {Promise<void>}
     */
    async fetchSysConfig() {
      try {
        const res = await getSysConfigList();
        if (res.code === 200 && Array.isArray(res.data)) {
          // 模糊匹配 L1/L2 商务政策
          const l1Config = res.data.find(
            (item) =>
              item.configType === "L1_BUSINESS_POLICY" ||
              item.configType === "L1" ||
              (item.configType && item.configType.toUpperCase().includes("L1"))
          );
          const l2Config = res.data.find(
            (item) =>
              item.configType === "L2_BUSINESS_POLICY" ||
              item.configType === "L2" ||
              (item.configType && item.configType.toUpperCase().includes("L2"))
          );
          if (l1Config) this.l1Policy = l1Config.configContent || "";
          if (l2Config) this.l2Policy = l2Config.configContent || "";
        }
      } catch (err) {
        console.error(err);
      }
    },

    /**
     * 格式化日期字符串
     * @param {string} str - 原始日期时间字符串
     * @returns {string} 替换连字符后的点号分割日期
     */
    formatDateStr(str) {
      if (!str) return "";
      const datePart = str.split(" ")[0]; // 去除时间
      return datePart.replace(/-/g, ".");
    },

    /**
     * 格式化授权期内有效业绩期
     * @param {string} start - 开始日期
     * @param {string} end - 结束日期
     * @returns {string} 拼接后的业绩有效期间文本
     */
    formatPeriod(start, end) {
      if (!start && !end) return "";
      return `${this.formatDateStr(start)}~${this.formatDateStr(end)}`;
    },

    /**
     * 格式化返利系数
     * @param {number|string} val - 返利系数值
     * @returns {string} 格式化后的百分比文本
     */
    formatCoefficient(val) {
      if (val === undefined || val === null || val === "") return "";
      if (typeof val === "string" && val.includes("%")) return val;
      const num = Number(val);
      if (Number.isNaN(num)) return val;
      if (num > 0 && num < 1) {
        return `${(num * 100).toFixed(0)}%`;
      }
      return `${num}%`;
    },

    /**
     * 格式化金额千分位
     * @param {number|string} val - 金额数值
     * @returns {string} 带千分位的金额显示
     */
    formatAmount(val) {
      const num = Number(String(val).replace(/,/g, ""));
      if (Number.isNaN(num)) return val;
      return num.toLocaleString("zh-CN");
    },
  },
};
</script>

<style lang="less" scoped>
.partnerPerformanceInfo {
  padding: 32px;
  background: #fff;
  min-height: 300px;
}
.partnerPerformanceInfoSubTabs {
  display: flex;
  margin-bottom: 32px;
}
.partnerPerformanceInfoSubTab {
  padding: 4px 16px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-size: 14px;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.2s, background 0.2s, border-color 0.2s;
  & + & {
    margin-left: -1px;
  }
  &:hover {
    color: #00d8c3;
  }
  &.is-active {
    color: #fff;
    background: #00d8c3;
    border-color: #00d8c3;
  }
}
.partnerPerformanceInfoStats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 32px;
}
.partnerPerformanceInfoStatCard {
  padding: 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  &--inspect {
    background: #f9fafb;
  }
  &--qualified {
    background: #f0fdf4;
  }
  &--unqualified {
    background: #fef2f2;
  }
}
.partnerPerformanceInfoStatLabel {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}
.partnerPerformanceInfoStatCard--qualified .partnerPerformanceInfoStatLabel,
.partnerPerformanceInfoStatCard--unqualified .partnerPerformanceInfoStatLabel {
  color: #475569;
}
.partnerPerformanceInfoStatValue {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}
.partnerPerformanceInfoStatUnit {
  font-size: 14px;
  font-weight: 400;
}
.partnerPerformanceInfoTable {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;

  /deep/ th.el-table__cell {
    background: linear-gradient(180deg, #f0fbf9 0%, #e2f6f3 100%) !important;
    color: #004d45;
    font-size: 14px;
    font-weight: 600;
    height: 38px;
    padding: 6px 0;
    border-bottom: 1px solid #c7eee7;
  }

  /deep/ td.el-table__cell {
    color: #475569;
    font-size: 14px;
    padding: 12px 0;
  }

  /deep/ .el-table__row--striped td.el-table__cell {
    background-color: #fafbfc;
  }

  /deep/ .el-table__body tr:hover > td.el-table__cell {
    background-color: #f0fdfa !important;
  }
}
.performanceInfoAmount {
  font-weight: 600;
  color: #0f172a;
  font-size: 15px;
}
.partnerPerformanceInfoPolicyWrap {
  margin-bottom: 24px;
  background: #f8fafc;
  border-radius: 4px;
  padding: 20px;
  border: 1px solid #f1f5f9;
}
.partnerPerformanceInfoPolicyRow {
  display: flex;
  font-size: 14px;
  line-height: 1.6;
  color: #334155;
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
  &.mt-12 {
    margin-top: 12px;
  }
  .policyLabel {
    color: #64748b;
    font-weight: 500;
    flex-shrink: 0;
  }
  .policyValue {
    color: #1e293b;
    font-weight: 500;
    word-break: break-all;
    &.highlight {
      color: #00bcd4;
      font-weight: 600;
      font-size: 16px;
    }
  }
}
.partnerPerformanceInfoTableEmpty {
  padding: 48px !important;
  color: #94a3b8;
  font-size: 14px;
  background: #fff;
  text-align: center;
}
.status-confirmed {
  color: #22c55e;
  font-weight: 500;
}
.status-unconfirmed {
  color: #f97316;
  font-weight: 500;
}
</style>
